/**
 * What one template's background looks like, resolved from three layers:
 *
 *   1. the template's OWN state — a per-template upload or a slider value
 *      the user set while the template was detached;
 *   2. a PRESET — a per-template value an admin saved into the default scene
 *      for this background (`customBgs[id]`, `bgOpacities[id]`,
 *      `bgBrightnesses[id]`), which applies with the chain intact;
 *   3. the SHARED background and sliders every template follows.
 *
 * A preset is the layer that makes "position this one banner's artwork, or
 * give it another image, and save it as the default" work without the user
 * ever seeing a detached chain. The rules the layers follow:
 *
 * - A preset image shows for a numbered gallery background only. A shared
 *   upload (bgOption "custom") is a stronger intent and replaces it.
 * - A per-template opacity or brightness applies whenever it is present, own
 *   or preset; the shared sliders move only the templates that have none.
 *   Detaching writes the banner's current values as its own (TemplateControls),
 *   so from that moment the shared sliders leave it alone.
 * - Re-attaching drops the template's own values and puts the presets back —
 *   `bgPresets` is the snapshot the default scene left for exactly that.
 *
 * Pure, so the rules are unit-tested without React.
 */
import {isIdentityTransform, type LooseBgTransform} from "libs/bakedBg";
import {isVideoSource} from "libs/utils";

export interface BgPreset {
  customBg?: string;
  opacity?: number;
  brightness?: number;
}

export interface TemplateBgInput {
  id: string;
  isLocked: boolean;
  bgOption: string | number | null | undefined;
  /** The shared/gallery/baked source the template would otherwise render. */
  sharedSrc: string | null | undefined;
  customBgs: Record<string, string | undefined> | undefined;
  customBgUploaded: Record<string, boolean | undefined> | undefined;
  bgOpacities: Record<string, number | undefined> | undefined;
  bgBrightnesses: Record<string, number | undefined> | undefined;
  bgOpacity: number | null | undefined;
  bgBrightness: number | null | undefined;
  bgPresets: Record<string, BgPreset | undefined> | undefined;
  /**
   * The banner's image mode from the scope table (libs/sceneScope.ts) on a
   * scopes channel; undefined elsewhere, where the chain and the upload flag
   * decide as before.
   */
  imageMode?: "own" | "preset";
}

export interface TemplateBgResolution {
  src: string;
  opacityPercent: number;
  brightnessPercent: number;
  /** True when a preset shapes this template while its chain is intact. */
  hasPreset: boolean;
}

/** A numbered gallery background: the only kind a banner keeps an image for. */
export const isGalleryOption = (bgOption: TemplateBgInput["bgOption"]) => {
  const n = Number(bgOption);
  return Number.isInteger(n) && n > 0;
};

/** The fields that decide whether a template shows an image of its own. */
export type TemplateImageInput = Pick<
  TemplateBgInput,
  "id" | "isLocked" | "bgOption" | "customBgs" | "customBgUploaded" | "imageMode"
>;

/**
 * The image of its own that the template shows — its upload, its image on a
 * scopes channel, or a preset — or null when it shows the shared background.
 * This is the first layer of resolveTemplateBackground.
 */
export function templateOwnImage({
  id,
  isLocked,
  bgOption,
  customBgs,
  customBgUploaded,
  imageMode,
}: TemplateImageInput): string | null {
  const ownImage = customBgs?.[id];
  if (!ownImage) return null;
  // Set apart on a scopes channel: the banner's image, chain or no chain.
  if (imageMode === "own") return ownImage;
  // The user's own upload for this template: only a detached template shows
  // it, exactly as before.
  if (customBgUploaded?.[id]) return isLocked ? ownImage : null;
  // A preset image: shows over a numbered background, chain intact or not.
  return isGalleryOption(bgOption) ? ownImage : null;
}

/**
 * A video or a GIF: an image that moves, known from its URL alone. The other
 * animated formats (SVG, PNG, WebP) need the file itself, which the upload
 * reads (bg-utils checkIfAnimated).
 */
export function isMovingMedia(src: string | null | undefined): boolean {
  if (!src) return false;
  if (isVideoSource(src)) return true;
  return /^data:image\/gif[;,]/i.test(src) || /\.gif(?:[?#]|$)/i.test(src);
}

export function resolveTemplateBackground({
  id,
  isLocked,
  bgOption,
  sharedSrc,
  customBgs,
  customBgUploaded,
  bgOpacities,
  bgBrightnesses,
  bgOpacity,
  bgBrightness,
  bgPresets,
  imageMode,
}: TemplateBgInput): TemplateBgResolution {
  const gallery = isGalleryOption(bgOption);
  const src =
    templateOwnImage({id, isLocked, bgOption, customBgs, customBgUploaded, imageMode}) ??
    sharedSrc ??
    "";

  const opacityPercent = bgOpacities?.[id] ?? bgOpacity ?? 100;
  const brightnessPercent = bgBrightnesses?.[id] ?? bgBrightness ?? 100;

  const preset = bgPresets?.[id];
  const hasPreset =
    !isLocked &&
    gallery &&
    Boolean(
      preset &&
        (preset.customBg !== undefined ||
          preset.opacity !== undefined ||
          preset.brightness !== undefined)
    );

  return {src, opacityPercent, brightnessPercent, hasPreset};
}

/**
 * The presets a default scene carries, one entry per template that has any.
 * `main` is the shared slot, not a template. Every other `customBgs` entry is
 * the banner's image for the background the default is saved under — an
 * image an admin uploaded into the banner too (`customBgUploaded` marks an
 * upload in the editing session only).
 */
export function buildBgPresets(scene: {
  customBgs?: Record<string, string | undefined> | null;
  customBgUploaded?: Record<string, boolean | undefined> | null;
  bgOpacities?: Record<string, number | undefined> | null;
  bgBrightnesses?: Record<string, number | undefined> | null;
}): Record<string, BgPreset> {
  const presets: Record<string, BgPreset> = {};
  const put = (id: string, patch: BgPreset) => {
    if (id === "main") return;
    presets[id] = {...presets[id], ...patch};
  };
  for (const [id, url] of Object.entries(scene.customBgs ?? {})) {
    if (url) put(id, {customBg: url});
  }
  for (const [id, value] of Object.entries(scene.bgOpacities ?? {})) {
    if (typeof value === "number") put(id, {opacity: value});
  }
  for (const [id, value] of Object.entries(scene.bgBrightnesses ?? {})) {
    if (typeof value === "number") put(id, {brightness: value});
  }
  return presets;
}

/** A file a user uploaded (POST /api/v3/uploads), not a prepared background. */
export const isUploadedImage = (url: string | null | undefined): boolean =>
  typeof url === "string" && url.includes("/cai/baker/uploads/");

/**
 * The upload id of an uploaded raster image (`/cai/baker/uploads/<id>.png`),
 * or null: for any other URL, and for an SVG or a video, which keep the
 * custom slot.
 */
export function rasterUploadId(url: string | null | undefined): string | null {
  const match = /\/cai\/baker\/uploads\/([0-9a-f]{12})\.(?:png|jpe?g|webp|gif|avif)(?:[?#]|$)/i.exec(
    url ?? ""
  );
  return match ? match[1]! : null;
}

/**
 * Is the banner's image a ready cut: an image the default scene gives it
 * already cut to its box, as FigBaker's Figma backgrounds are? Only a ready cut
 * has no positioning controls (Sergey, 2026-10-16). Every other image keeps
 * them, also when it is saved as a default and baked: the catalog artwork, an
 * upload promoted to a tile, a file uploaded into the banner.
 *
 * A ready cut is the banner's preset image (`customBgs[id]` that is not an
 * upload of this session), it is not an uploaded file, and no crop moves it.
 * A default save of a ready cut writes the identity crop, so that crop counts
 * as none.
 */
export function isReadyCut({
  src,
  image,
  uploaded,
  crop,
}: {
  /** The image the banner shows, or the image its baked cut was made from. */
  src: string | null | undefined;
  /** The banner's `customBgs[id]`. */
  image: string | null | undefined;
  /** The banner's `customBgUploaded[id]`. */
  uploaded: boolean | undefined;
  /** The crop on the image; null when no layer gives it one. */
  crop: LooseBgTransform | null | undefined;
}): boolean {
  if (!image || uploaded || src !== image || isUploadedImage(image)) return false;
  return !crop || isIdentityTransform(crop);
}

/**
 * Does the template show an image of its own, not the one the default scene
 * gave it? True for an upload into the banner (`customBgUploaded`) and for any
 * `customBgs` image that is not the default's preset — a sidebar upload into a
 * numbered background writes one for every banner. Such an image replaces the
 * banner's baked background (libs/bakedBg.ts).
 */
export function hasOwnTemplateImage({
  id,
  customBgs,
  customBgUploaded,
  bgPresets,
}: Pick<TemplateBgInput, "id" | "customBgs" | "customBgUploaded" | "bgPresets">): boolean {
  if (customBgUploaded?.[id]) return true;
  const image = customBgs?.[id];
  return Boolean(image) && image !== bgPresets?.[id]?.customBg;
}
